import React, { useEffect, useRef, useState } from 'react';
import api from '../../../api';

type Chat = { sessionId: string; chatSessionId: string; title: string; archived_at?: string | null; archive_updated_at?: string | null };
type Repository = { repository_id: string; repo_owner: string; repo_name: string; archived_at: string | null; updated_at: string };
type RepositoryState = { repositories: Repository[]; candidates: Repository[]; executionAvailable: false };
type Membership = { projectId:string;chatSessionId:string;chatUpdatedAt:string;wrapperUpdatedAt:string };

/** Administration changes associations only. The chat's authenticated runtime
 * context determines which linked repositories are available for execution. */
export function ProjectChatsPanel({ projectId, archived, go }: { projectId: string; archived: boolean; go: (page: string) => void }) {
  const [chats, setChats] = useState<Chat[]>([]);
  const [archiveAvailable,setArchiveAvailable] = useState(false);
  const [selected, setSelected] = useState<Chat | null>(null);
  const [title, setTitle] = useState('');
  const [repos, setRepos] = useState<RepositoryState | null>(null);
  const [candidate, setCandidate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [move,setMove]=useState<{chat:Chat;membership:Membership;projects:Array<{project_id:string;display_name:string}>}|null>(null);
  const [destination,setDestination]=useState('');
  const [notice,setNotice]=useState('');
  const generation = useRef(0);
  const scopeGeneration=useRef(0);
  const currentProject = useRef(projectId);
  currentProject.current = archived ? '' : projectId;

  useEffect(() => {
    scopeGeneration.current++;
    currentProject.current = archived ? '' : projectId;
    const request = ++generation.current;
    setChats([]); setArchiveAvailable(false); setSelected(null); setRepos(null); setCandidate(''); setError(null); setBusy(false);setMove(null);setDestination('');setNotice('');
    if (archived) return;
    void api.getSubscriberChats(projectId,true).then(result => { if (request === generation.current) { setChats(result.chats); setArchiveAvailable(result.archiveAvailable === true); } })
      .catch(err => { if (request === generation.current) setError(err instanceof Error ? err.message : 'Could not load chats'); });
    return () => { generation.current++;scopeGeneration.current++; currentProject.current = ''; };
  }, [projectId, archived]);

  async function loadRepositories(chat: Chat) {
    const request = ++generation.current;
    setSelected(chat); setTitle(chat.title); setRepos(null); setCandidate(''); setError(null);setMove(null);setDestination('');
    try {
      const result = await api.get<RepositoryState>(`/subscriber-chat/chats/${encodeURIComponent(chat.sessionId)}/repositories`);
      if (request === generation.current) setRepos(result);
    } catch (err) { if (request === generation.current) setError(err instanceof Error ? err.message : 'Could not load repositories'); }
  }
  async function change(action: () => Promise<void>) {
    if (busy) return;
    const project = projectId;
    const scope=scopeGeneration.current;
    setBusy(true); setError(null);
    try { await action(); } catch (err) { if (currentProject.current === project&&scope===scopeGeneration.current) setError(err instanceof Error ? err.message : 'Could not save changes'); }
    finally { if (currentProject.current === project&&scope===scopeGeneration.current) setBusy(false); }
  }
  return <section className="card" style={{ marginBottom: 16 }} aria-label="Project chats">
    <h3>Chats</h3>
    {archived ? <p>Restore this project to reopen and organize its chats. Existing histories are preserved.</p> : <>
      <button className="btn btn-sm" disabled={busy} onClick={() => void change(async () => {
        const created = await api.createSubscriberChat(projectId);
        if (currentProject.current !== projectId) return;
        const result = await api.getSubscriberChats(projectId,true);
        if (currentProject.current !== projectId) return;
        setChats(result.chats); setArchiveAvailable(result.archiveAvailable === true); go(`subscriber-chat:chat:${created.chatSessionId}`);
      })}>New chat</button>
      {chats.length === 0 && <p>No chats yet.</p>}
      {archiveAvailable ? <p>Archiving hides chats from active navigation. Work already in progress continues.</p> : <p>Chat archiving is unavailable right now.</p>}
      {chats.map(chat => <div key={chat.sessionId} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn btn-sm" disabled={busy || !!chat.archived_at} onClick={() => go(`subscriber-chat:chat:${chat.chatSessionId}`)}>{chat.title || 'New chat'}{chat.archived_at ? ' (archived)' : ''}</button>
        <button className="btn btn-sm" disabled={busy || !!chat.archived_at} onClick={() => void loadRepositories(chat)} aria-label={`Manage ${chat.title}`}>Manage</button>
        <button className="btn btn-sm" disabled={busy} aria-label={`Move ${chat.title}`} onClick={() => void change(async()=>{
          const request=++generation.current;setMove(null);setSelected(null);setRepos(null);setDestination('');setNotice('');
          const [membership,result]=await Promise.all([
            api.get<Membership>(`/subscriber-chat/chats/${encodeURIComponent(chat.sessionId)}/membership`),
            api.get<{projects:Array<{project_id:string;display_name:string}>}>('/projects'),
          ]);
          if(currentProject.current!==projectId||request!==generation.current)return;
          if(membership.projectId!==projectId||membership.chatSessionId!==chat.chatSessionId)throw new Error('This chat moved. Reload its project before trying again.');
          setMove({chat,membership,projects:result.projects.filter(project=>project.project_id!==projectId)});
        })}>Move chat…</button>
        {archiveAvailable && <button className="btn btn-sm" disabled={busy} aria-label={`${chat.archived_at ? 'Restore' : 'Archive'} ${chat.title}`} onClick={() => void change(async () => {
          await api.archiveSubscriberChat(chat.sessionId,!chat.archived_at,chat.archive_updated_at ?? null);
          if (currentProject.current !== projectId) return;
          const result = await api.getSubscriberChats(projectId,true);
          if (currentProject.current !== projectId) return;
          setChats(result.chats); setArchiveAvailable(result.archiveAvailable === true);
          if (selected?.sessionId === chat.sessionId) { setSelected(null);setRepos(null); }
        })}>{chat.archived_at ? 'Restore chat' : 'Archive chat'}</button>}
      </div>)}
      {move&&<div style={{marginTop:12}}>
        <p>Move {move.chat.title} to another project. Its history, repositories and work stay with the same chat.</p>
        <select aria-label="Destination project" disabled={busy} value={destination} onChange={event=>setDestination(event.target.value)}>
          <option value="">Choose a project</option>
          {move.projects.map(project=><option key={project.project_id} value={project.project_id}>{project.display_name||project.project_id}</option>)}
        </select>
        <button className="btn btn-sm" disabled={busy||!destination} onClick={()=>void change(async()=>{
          const request=++generation.current;const observed=move;
          // Invalidate this revision after one submission, including an ambiguous
          // response. Reopening reads membership again instead of replaying blindly.
          setMove(null);setDestination('');
          await api.moveSubscriberChat(observed.chat.sessionId,{projectId:destination,
            expectedProjectId:observed.membership.projectId,expectedChatUpdatedAt:observed.membership.chatUpdatedAt,expectedWrapperUpdatedAt:observed.membership.wrapperUpdatedAt});
          if(currentProject.current!==projectId||request!==generation.current)return;
          const result=await api.getSubscriberChats(projectId,true);
          if(currentProject.current!==projectId||request!==generation.current)return;
          setChats(result.chats);setArchiveAvailable(result.archiveAvailable===true);setNotice('Chat moved. Open its destination project to continue.');
        })}>Move to project</button>
        <button className="btn btn-sm" disabled={busy} onClick={()=>{setMove(null);setDestination('');}}>Cancel move</button>
      </div>}
      {selected && <div style={{ marginTop: 12 }}>
        <label>Chat name <input aria-label="Chat name" value={title} maxLength={120} disabled={busy} onChange={event => setTitle(event.target.value)} /></label>
        <button className="btn btn-sm" disabled={busy || !title.trim()} onClick={() => void change(async () => {
          const updated = await api.renameSubscriberChat(selected.sessionId, title.trim());
          if (currentProject.current !== projectId) return;
          setChats(old => old.map(chat => chat.sessionId === updated.id ? { ...chat, title: updated.title } : chat));
          setSelected(old => old?.sessionId === updated.id ? { ...old, title: updated.title } : old);
        })}>Save chat name</button>
        <h4>Repositories</h4>
        <p>Link repositories here, then open the chat to choose an available repository. Repositories linked after its session starts cannot run in that chat yet.</p>
        {repos?.repositories.map(repo => <div key={repo.repository_id}>
          {repo.repo_owner}/{repo.repo_name}{repo.archived_at ? ' (archived link)' : ''}{' '}
          <button className="btn btn-sm" disabled={busy} onClick={() => void change(async () => {
            await api.archiveChatRepository(selected.sessionId, repo.repository_id, !repo.archived_at, repo.updated_at);
            if (currentProject.current === projectId) await loadRepositories(selected);
          })}>{repo.archived_at ? 'Restore link' : 'Archive link'}</button>
        </div>)}
        {repos && <><select aria-label="Repository to associate" value={candidate} disabled={busy} onChange={event => setCandidate(event.target.value)}>
          <option value="">Choose an owned repository</option>
          {repos.candidates.filter(repo => !repos.repositories.some(link => link.repository_id === repo.repository_id)).map(repo => <option key={repo.repository_id} value={repo.repository_id}>{repo.repo_owner}/{repo.repo_name}</option>)}
        </select><button className="btn btn-sm" disabled={busy || !candidate} onClick={() => void change(async () => {
          await api.associateChatRepository(selected.sessionId, candidate);
          if (currentProject.current === projectId) await loadRepositories(selected);
        })}>Associate repository</button></>}
      </div>}
    </>}
    {error && <p role="alert">{error}</p>}
    {notice&&<p role="status">{notice}</p>}
  </section>;
}
